import { Link, type LinkProps } from '@tanstack/react-router'
import type { AnchorHTMLAttributes, ReactNode } from 'react'

type AppLinkProps = {
  /** A plain path, optionally with a query string (`/fotograf/vydelky?info=odmeny`). */
  href: string
  children?: ReactNode
  className?: string
  onClick?: () => void
  'aria-label'?: string
  'aria-current'?: AnchorHTMLAttributes<HTMLAnchorElement>['aria-current']
}

/**
 * The app's link. Screens pass a plain `href` string — most of them come from
 * mock records, not from the typed route tree — and this is the ONE place that
 * hands it to the router.
 *
 * The query string is split off and passed as `search`, because the router
 * builds the URL from `to` + `search` and would otherwise treat `?info=odmeny`
 * as part of the pathname. A link with no query clears the search params, which
 * is exactly what a sheet's `closeHref` wants.
 *
 * When a screen gets wired to real data, its links move to the typed form
 * (`to="/spravce/klienti/$client" params={{ client: slug }}`) and stop coming
 * through here; the casts stay contained until then.
 */
export function AppLink({ href, ...rest }: AppLinkProps) {
  const [pathname, query] = href.split('?')
  const search = query ? Object.fromEntries(new URLSearchParams(query)) : undefined

  return <Link to={pathname as LinkProps['to']} search={search as LinkProps['search']} {...rest} />
}
